import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import SplashScreen from "./components/SplashScreen";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import UsersPage from "./pages/Users";
import RolesPage from "./pages/Roles";
import AuditPage from "./pages/Audit";
import Profile from "./pages/Profile";
import Notebook from "./pages/Notebook";
import Tasks from "./pages/Tasks";
import Calendar from "./pages/Calendar";
import Vacations from "./pages/Vacations";
import Clients from "./pages/Clients";
import Prospects from "./pages/Prospects";
import Visits from "./pages/Visits";
import Approvals from "./pages/Approvals";
import Commissions from "./pages/Commissions";
import StockLookup from "./pages/StockLookup";
import Tracking from "./pages/Tracking";
import Pipeline from "./pages/Pipeline";
import Quotations from "./pages/Quotations";
import Orders from "./pages/Orders";
import Deliveries from "./pages/Deliveries";
import Products from "./pages/Products";
import Suppliers from "./pages/Suppliers";
import Inventory from "./pages/Inventory";
import Purchases from "./pages/Purchases";
import Imports from "./pages/Imports";
import Invoices from "./pages/Invoices";
import Statements from "./pages/Statements";
import Semaforo from "./pages/Semaforo";
import Profitability from "./pages/Profitability";
import Receipts from "./pages/Receipts";
import AccountsReceivable from "./pages/AccountsReceivable";
import ManualFigures from "./pages/ManualFigures";
import PettyCash from "./pages/PettyCash";
import Treasury from "./pages/Treasury";
import SupplierPayments from "./pages/SupplierPayments";
import RoutesPage from "./pages/Routes";
import Service from "./pages/Service";
import Warranties from "./pages/Warranties";
import Closing from "./pages/Closing";
import CashFlow from "./pages/CashFlow";
import RegistroDiario from "./pages/RegistroDiario";
import CentroFacturas from "./pages/CentroFacturas";
import FacturaRapida from "./pages/FacturaRapida";
import MetasVenta from "./pages/MetasVenta";
import Payroll from "./pages/Payroll";
import Reportes from "./pages/Reportes";
import Analytics from "./pages/Analytics";
import MisVouchers from "./pages/MisVouchers";
import Funnel from "./pages/Funnel";
import Bandeja from "./pages/Bandeja";
import Chat from "./pages/Chat";
import Help from "./pages/Help";
import TransferReport from "./pages/TransferReport";
import Placeholder from "./pages/Placeholder";
import { NotebookTab } from "./components/NotebookTab";

function Loader() {
  return <SplashScreen />;
}

function Guard({ perm, title, children }) {
  const { user, loading, can } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  const perms = Array.isArray(perm) ? perm : perm ? [perm] : [];
  if (perms.length && !perms.some((p) => can(p))) {
    return (
      <Layout title="Acceso denegado">
        <Placeholder title="Sin permiso" description="No tienes permiso para acceder a esta sección. Contacta a tu administrador." />
      </Layout>
    );
  }
  return <Layout title={title}>{children}</Layout>;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function App() {
  const [booting, setBooting] = useState(true);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setFading(true), 1200);
    const t2 = setTimeout(() => setBooting(false), 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Enter y flechas ↑/↓ navegan entre campos dentro de un formulario o diálogo.
  useEffect(() => {
    const getNodes = (container) => Array.from(container.querySelectorAll("input, select, textarea, [role='combobox']"))
      .filter((el) => !el.disabled && el.type !== "hidden" && el.offsetParent !== null);
    const focusNode = (n) => { n.focus(); if (n.tagName === "INPUT" && typeof n.select === "function") { try { n.select(); } catch (err) {} } };
    const onKey = (e) => {
      if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (!t) return;
      if (t.closest("[cmdk-root]")) return; // el buscador global usa Enter/flechas
      const container = t.closest("form, [role='dialog']");

      if (e.key === "Enter") {
        if (t.tagName !== "INPUT") return; // textarea = salto de línea; combobox = abre nativo
        const type = (t.getAttribute("type") || "text").toLowerCase();
        if (["checkbox", "radio", "button", "submit", "file"].includes(type)) return;
        if (t.hasAttribute("data-enter-ignore")) return;

        // Dentro de una tabla de líneas: avanza de casilla; en la última casilla agrega otra línea.
        const li = t.closest("[data-lineitems]");
        if (li) {
          const liNodes = getNodes(li);
          const i = liNodes.indexOf(t);
          if (i !== -1) {
            if (liNodes[i + 1]) { e.preventDefault(); focusNode(liNodes[i + 1]); return; }
            const scope = container || document;
            const addBtn = scope.querySelector("[data-additem]");
            if (addBtn) {
              e.preventDefault();
              const before = liNodes.length;
              addBtn.click();
              setTimeout(() => { const after = getNodes(li); if (after[before]) focusNode(after[before]); }, 60);
              return;
            }
          }
        }

        if (!container) return;
        const nodes = getNodes(container);
        const idx = nodes.indexOf(t);
        if (idx === -1) return;
        const next = nodes[idx + 1];
        if (next) { e.preventDefault(); focusNode(next); }
        return;
      }

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (t.tagName !== "INPUT") return;
        const type = (t.getAttribute("type") || "text").toLowerCase();
        if (!["text", "email", "tel", "password", "search"].includes(type)) return; // no romper number/date/time
        if (t.getAttribute("role") === "combobox" || t.hasAttribute("data-enter-ignore") || !container) return;
        const nodes = getNodes(container);
        const idx = nodes.indexOf(t);
        if (idx === -1) return;
        const target = e.key === "ArrowDown" ? nodes[idx + 1] : nodes[idx - 1];
        if (target) { e.preventDefault(); focusNode(target); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
        {booting && <SplashScreen fadeOut={fading} />}
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/" element={<Guard perm="dashboard:view" title="Panel Principal"><Dashboard /></Guard>} />
            <Route path="/chat" element={<Guard perm="profile:view" title="Chat Interno"><Chat /></Guard>} />
            <Route path="/ayuda" element={<Guard perm="profile:view" title="Ayuda"><Help /></Guard>} />
            <Route path="/perfil" element={<Guard perm="profile:view" title="Mi Perfil"><Profile /></Guard>} />
            <Route path="/libreta" element={<Guard perm="notebook:view" title="Mi Libreta"><Notebook /></Guard>} />
            <Route path="/tareas" element={<Guard perm="tasks:view" title="Mis Tareas"><Tasks /></Guard>} />
            <Route path="/agenda" element={<Guard perm="calendar:view" title="Agenda comercial"><Tracking /></Guard>} />
            <Route path="/vacaciones" element={<Guard perm="vacations:view" title="Vacaciones y Ausencias"><Vacations /></Guard>} />
            <Route path="/clientes" element={<Guard perm="clients:view" title="Clientes"><Clients /></Guard>} />
            <Route path="/prospectos" element={<Guard perm="prospects:view" title="Prospectos"><Prospects /></Guard>} />
            <Route path="/visitas" element={<Guard perm="visits:view" title="Visitas"><Visits /></Guard>} />
            <Route path="/comisiones" element={<Guard perm="commissions:view" title="Comisiones"><Commissions /></Guard>} />
            <Route path="/existencias" element={<Guard perm="stock:view" title="Consulta de Existencias"><StockLookup /></Guard>} />
            <Route path="/seguimiento" element={<Guard perm="tracking:view" title="Seguimiento"><Tracking /></Guard>} />
            <Route path="/pipeline" element={<Guard perm="tracking:view" title="Pipeline comercial"><Pipeline /></Guard>} />
            <Route path="/aprobaciones" element={<Guard perm="approvals:view" title="Aprobaciones"><Approvals /></Guard>} />
            <Route path="/cotizaciones" element={<Guard perm="quotations:view" title="Cotizaciones"><Quotations /></Guard>} />
            <Route path="/pedidos" element={<Guard perm="orders:view" title="Pedidos"><Orders /></Guard>} />
            <Route path="/envios" element={<Guard perm="deliveries:view" title="Notas de Envío"><Deliveries /></Guard>} />
            <Route path="/facturas" element={<Guard perm="invoices:view" title="Ventas"><Invoices /></Guard>} />
            <Route path="/factura-rapida" element={<Guard perm="invoices:quick" title="Factura Rápida"><FacturaRapida /></Guard>} />
            <Route path="/metas" element={<Guard perm="salesgoals:view" title="Metas de Ventas"><MetasVenta /></Guard>} />
            <Route path="/cuentas-por-cobrar" element={<Guard perm={["statements:view", "receipts:view"]} title="Cuentas por Cobrar"><AccountsReceivable /></Guard>} />
            <Route path="/cifras-manuales" element={<Guard perm="manualfigures:view" title="Cifras Manuales"><ManualFigures /></Guard>} />
            <Route path="/estados-cuenta" element={<Guard perm="statements:view" title="Estados de Cuenta"><Statements /></Guard>} />
            <Route path="/semaforo" element={<Guard perm="invoices:view" title="Semáforo Financiero"><Semaforo /></Guard>} />
            <Route path="/rentabilidad" element={<Guard perm="invoices:view" title="Rentabilidad"><Profitability /></Guard>} />
            <Route path="/productos" element={<Guard perm="inventory:view" title="Productos / Inventario"><Products /></Guard>} />
            <Route path="/proveedores" element={<Guard perm="suppliers:view" title="Proveedores"><Suppliers /></Guard>} />
            <Route path="/inventario" element={<Guard perm="inventory:view" title="Inventario / Kardex"><Inventory /></Guard>} />
            <Route path="/compras" element={<Guard perm="purchases:view" title="Órdenes de Compra"><Purchases /></Guard>} />
            <Route path="/importaciones" element={<Guard perm="imports:view" title="Importaciones"><Imports /></Guard>} />
            <Route path="/recibos" element={<Guard perm="receipts:view" title="Recibos de Caja"><Receipts /></Guard>} />
            <Route path="/registro-diario" element={<Guard perm="docs:view" title="Registro Diario"><RegistroDiario /></Guard>} />
            <Route path="/centro-facturas" element={<Guard perm="doccenter:view" title="Compras y servicios"><CentroFacturas /></Guard>} />
            <Route path="/pago-colaboradores" element={<Guard perm="payroll:view" title="Pago a Colaboradores"><Payroll /></Guard>} />
            <Route path="/reportes" element={<Guard perm="reports:view" title="Centro de Reportes"><Reportes /></Guard>} />
            <Route path="/analitica" element={<Guard perm="analytics:view" title="Analítica"><Analytics /></Guard>} />
            <Route path="/reporte-transferencias" element={<Guard perm="reports:view" title="Reporte de Transferencias"><TransferReport /></Guard>} />
            <Route path="/mis-vouchers" element={<Guard perm="profile:view" title="Mis Vouchers"><MisVouchers /></Guard>} />
            <Route path="/embudo" element={<Guard perm="prospects:view" title="Embudo Comercial"><Funnel /></Guard>} />
            <Route path="/bandeja" element={<Guard perm="profile:view" title="Bandeja de Documentos"><Bandeja /></Guard>} />
            <Route path="/pagos" element={<Guard perm="payments:view" title="Pagos a Proveedores"><SupplierPayments /></Guard>} />
            <Route path="/caja-chica" element={<Guard perm="pettycash:view" title="Caja Chica"><PettyCash /></Guard>} />
            <Route path="/tesoreria" element={<Guard perm="treasury:view" title="Tesorería / Bancos"><Treasury /></Guard>} />
            <Route path="/flujo-caja" element={<Guard perm="treasury:view" title="Flujo de Caja"><CashFlow /></Guard>} />
            <Route path="/cierre-mensual" element={<Guard perm="closing:view" title="Cierre Mensual"><Closing /></Guard>} />
            <Route path="/rutas" element={<Guard perm="routes:view" title="Rutas / Mensajería"><RoutesPage /></Guard>} />
            <Route path="/servicio-tecnico" element={<Guard perm="service:view" title="Servicio Técnico"><Service /></Guard>} />
            <Route path="/garantias" element={<Guard perm="warranties:view" title="Garantías y Reposiciones"><Warranties /></Guard>} />
            <Route path="/usuarios" element={<Guard perm="users:view" title="Usuarios"><UsersPage /></Guard>} />
            <Route path="/roles" element={<Guard perm="roles:view" title="Roles y Permisos"><RolesPage /></Guard>} />
            <Route path="/auditoria" element={<Guard perm="audit:view" title="Auditoría"><AuditPage /></Guard>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <NotebookTab />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
